/**
 * <HeroCarousel />
 * ----------------
 * Smooth cross-fade carousel for the home hero image.
 *  • Same dimensions / same place as the existing static image — only the source rotates.
 *  • All slides are pre-loaded so the fade is instant and seamless.
 *  • The FIRST slide is rendered with fetchPriority="high" + loading="eager" for LCP.
 *  • Subsequent slides use loading="lazy" so they don't compete with the hero.
 *  • Respects prefers-reduced-motion: stops rotating, keeps a static first slide.
 *  • Pause-on-hover (desktop) so users can take a closer look.
 *  • Dots + numeric counter (1/5) bottom-right for orientation.
 *
 *  Usage:
 *      <HeroCarousel slides={SLIDES} intervalMs={6000} />
 */
import React, { useEffect, useRef, useState } from "react";

const HeroCarousel = ({ slides, intervalMs = 6000 }) => {
    const [index, setIndex] = useState(0);
    const hoverRef = useRef(false);
    const reducedMotion = useRef(
        typeof window !== "undefined" &&
            window.matchMedia &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );

    useEffect(() => {
        if (reducedMotion.current || slides.length <= 1) return;
        const id = setInterval(() => {
            if (!hoverRef.current) {
                setIndex((i) => (i + 1) % slides.length);
            }
        }, intervalMs);
        return () => clearInterval(id);
    }, [slides.length, intervalMs]);

    return (
        <div
            className="absolute inset-0 overflow-hidden"
            onMouseEnter={() => (hoverRef.current = true)}
            onMouseLeave={() => (hoverRef.current = false)}
            data-testid="hero-carousel"
            aria-label="Kerala destinations slideshow"
        >
            {slides.map((s, i) => (
                <img
                    key={s.src}
                    src={s.src}
                    alt={s.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-opacity duration-[2000ms] ease-in-out will-change-[opacity]"
                    style={{ opacity: i === index ? 1 : 0 }}
                    fetchPriority={i === 0 ? "high" : "low"}
                    loading={i === 0 ? "eager" : "lazy"}
                    decoding="async"
                    width="1200"
                    height="1500"
                    data-testid={`hero-slide-${i}`}
                    data-slot={s.slot}
                    aria-hidden={i === index ? "false" : "true"}
                />
            ))}

            {/* dots + counter */}
            {slides.length > 1 && (
                <div
                    className="absolute bottom-3 right-3 flex items-center gap-2 bg-black/35 backdrop-blur-sm rounded-full pl-3 pr-2.5 py-1.5 text-white"
                    data-testid="hero-carousel-controls"
                >
                    <ul className="flex items-center gap-1.5" role="tablist" aria-label="Hero slide selector">
                        {slides.map((s, i) => (
                            <li key={s.src}>
                                <button
                                    type="button"
                                    onClick={() => setIndex(i)}
                                    aria-label={`Show slide ${i + 1}: ${s.slot}`}
                                    aria-selected={i === index}
                                    className={`block rounded-full transition-all ${
                                        i === index ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/55 hover:bg-white/85"
                                    }`}
                                    data-testid={`hero-dot-${i}`}
                                />
                            </li>
                        ))}
                    </ul>
                    <span className="text-[10px] font-mono tabular-nums opacity-80 ml-1">
                        {index + 1}/{slides.length}
                    </span>
                </div>
            )}
        </div>
    );
};

export default HeroCarousel;
